import { Component, Injector, Input, OnChanges, OnDestroy, Type, ViewChild } from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { PartnerLogoService } from 'src/app/shared/services/partner-logo/partner-logo.service';
import { environment } from 'src/environments/environment';
import { WorkflowService } from 'src/app/shared/services/workflow/workflow.service';
import axios, { AxiosError } from 'axios';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { DiscardCaseComponent } from 'src/app/shared/workflow-dialogs/discard-case/discard-case.component';
import { CaseTableItem } from '../types';
import { MatSort, Sort } from '@angular/material/sort';
import { PayeeCasesService } from '../payee-cases.service';

export const ADDITIONAL_COLUMN_CONTEXT = 'ADDITIONAL_COLUMN_CONTEXT';

export type AdditionalColumn = {
  columnDef: string;
  sortable: boolean;
  header: Observable<string>;
  cell: Type<any>;
};

export type AdditionalAction<T = CaseTableItem> = {
  label: Observable<string>;
  show: (element: T) => boolean;
  onClick: (element: T) => void;
};

export type AdditionalColumnContext<T = CaseTableItem> = {
  element: T;
  elementChange: Subject<void>;
};

@Component({
  selector: 'payee-cases-table',
  templateUrl: './cases-table.component.html',
  styleUrls: ['./cases-table.component.scss'],
  providers: [MatTable],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class CasesTableComponent implements OnChanges, OnDestroy {
  private readonly contexts = new Map<string, Injector>();
  private readonly destroy = new Subject<void>();

  @Input() allowEdit = true;
  @Input() allowDelete = true;

  @Input() dataSource: MatTableDataSource<CaseTableItem>;
  @Input() additionalColumns: AdditionalColumn[] = [];
  @Input() additionalActions: AdditionalAction[] = [];

  @ViewChild(MatSort) set sort(sort: MatSort) {
    const sortBy = this.payeeCasesService.sort_by.value;
    if (sortBy) {
      setTimeout(() => {
        sort.sort({
          disableClear: false,
          id: sortBy,
          start: this.payeeCasesService.sort_direction.value,
        });
      });
    }
  }

  readonly loading = new Set<CaseTableItem>();

  private lockExpandedElement = false;
  expandedElement: CaseTableItem | null = null;

  private readonly baseColumns = [
    'payee_case_reference_id',
    'started_at',
    'parties',
    'state',
    'jurisdiction',
    'partner',
    'remaining_claim',
    'original_claim',
    // 'last_event',
    // 'deadlines',
    'actions',
  ];

  displayedColumns = this.baseColumns;

  get getPartnerLogoName() { return this.partnerLogoService.getPartnerMiniLogoName; }
  get environment() { return environment; }

  constructor(
    private partnerLogoService: PartnerLogoService,
    private workflowService: WorkflowService,
    private router: Router,
    private dialog: MatDialog,
    private injector: Injector,
    private payeeCasesService: PayeeCasesService,
  ) { }

  ngOnChanges(): void {
    this.displayedColumns = this.baseColumns
      .slice(0, this.baseColumns.length - 1)
      .concat(this.additionalColumns.map(c => c.columnDef))
      .concat(['actions']);
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  onSortChange(sort: Sort): void {
    this.payeeCasesService.sort_by.next(sort.direction ? sort.active : null);
    this.payeeCasesService.sort_direction.next(sort.direction);
  }

  onActionsMenuOpen(): void {
    this.lockExpandedElement = true;
  }

  onActionsMenuClosed(): void {
    this.lockExpandedElement = false;
  }

  filterHistoricCase(element: CaseTableItem): void {
    this.payeeCasesService.payee_case_reference_id.next(element.payee_case_reference_id);
  }

  updateExpandedElement(element: CaseTableItem): void {
    if (this.lockExpandedElement) {
      return;
    }

    if (this.expandedElement === element) {
      this.expandedElement = null;
    } else {
      this.expandedElement = element;
    }
  }

  editCase(element: CaseTableItem): void {
    this.router.navigateByUrl(`/case/${element.id}`);
  }

  deleteCase(element: CaseTableItem): void {
    const ref = this.dialog.open(DiscardCaseComponent, {
      data: { id: element.id }
    });

    ref.afterClosed()
      .pipe(takeUntil(this.destroy))
      .subscribe(async (result: boolean) => {
        if (!result) {
          return;
        }

        try {
          const url = environment.baseUrl + `/api/payee-case/${element.id}`;
          await axios.delete(url);
          element.deleted_at = (new Date()).toDateString();

          if (this.payeeCasesService.payee_case_reference_id.value) {
            this.payeeCasesService.payee_case_reference_id.next(null);
          } else {
            await this.payeeCasesService.setCases();
          }
        } catch (error: AxiosError | unknown) {
          if (error instanceof AxiosError) {
            console.error('Network error while deleting case', error.response?.data);
          } else {
            console.error('Unknown error while deleting case', error);
          }
        }
      });
  }

  additionalColumnTrackBy(_: number, element: CaseTableItem): string {
    return `payee-cases-table-${element.id}`;
  }

  getAdditionalColumnContext(element: CaseTableItem): Injector {
    if (this.contexts.has(element.id)) {
      return this.contexts.get(element.id)!;
    }

    const elementChange = new Subject<void>();
    elementChange
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.payeeCasesService.setCasesSubject.next());

    const context: AdditionalColumnContext = {
      element,
      elementChange,
    };

    const injector = Injector.create({
      providers: [{
        provide: ADDITIONAL_COLUMN_CONTEXT,
        useValue: context,
      }],
      parent: this.injector,
    });
    this.contexts.set(element.id, injector);
    return injector;
  }

  async runTransitionWithDialog(element: CaseTableItem, event: CaseTableItem['actions'][number]): Promise<void> {
    if (this.loading.has(element)) {
      console.warn('Case already doing a transition');
      return;
    }

    try {
      this.loading.add(element);

      const res = await this.workflowService.runTransitionWithDialog({
        transitionId: event.id,
        caseId: element.id,
        dialog: event.dialog,
      });

      if (!res) {
        return;
      }

      this.payeeCasesService.setCasesSubject.next();
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while running transition', error.response?.data);
      } else {
        console.error('Unknown error while running transition', error);
      }
    } finally {
      this.loading.delete(element);
    }
  }
}
